import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Input, Textarea, Select } from "@/components/kit";
import { AddressLookup } from "@/components/AddressLookup";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, createBooking } from "@/lib/driverData";
import { useDraftPersistence, clearDraft } from "@/lib/useDraftPersistence";
import { SERVICE_TYPES } from "@/lib/vda";

export default function DriverBook() {
  const nav = useNavigate();
  const { data: ctx, loading } = useAsync(() => loadDriverContext(), []);
  const [form, setForm] = useState({
    service_type: "full_service",
    mileage: "",
    preferred_date_1: "",
    preferred_date_2: "",
    preferred_date_3: "",
    collection_required: false,
    address: {},
    courtesy_vehicle_required: false,
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draftScope = ctx?.vehicle?.id ? `book_${ctx.vehicle.id}_service_repair` : null;
  useDraftPersistence({
    scope: draftScope, form, setForm, enabled: !!draftScope,
    onRestore: () => toast.info("Restored your previous progress"),
  });

  const baseline = ctx?.vehicle?.current_mileage ?? null;
  const mileageWarning = useMemo(() => {
    if (!form.mileage || baseline == null) return null;
    const m = Number(form.mileage);
    if (m < baseline) return `That's lower than our last recorded mileage (${baseline.toLocaleString()}). Please double-check.`;
    if (m - baseline > 50000) return `That's a large jump from ${baseline.toLocaleString()}. Please double-check.`;
    return null;
  }, [form.mileage, baseline]);

  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const chosen = SERVICE_TYPES.find((s) => s.value === form.service_type) || SERVICE_TYPES[0];
      const details = {
        flow: "service_repair",
        service_type: form.service_type,
        service_type_label: chosen.label,
      };
      if (form.collection_required && form.address?.address_text) details.collection_address = form.address;
      const payload = {
        request_type: chosen.request_type,
        request_category: "service_repair",
        request_details: details,
        description: form.description || null,
        mileage: form.mileage ? Number(form.mileage) : null,
        preferred_date_1: form.preferred_date_1 || null,
        preferred_date_2: form.preferred_date_2 || null,
        preferred_date_3: form.preferred_date_3 || null,
        location_text: form.address?.address_text || null,
        postcode: form.address?.postcode || null,
        collection_required: form.collection_required,
        courtesy_vehicle_required: form.courtesy_vehicle_required,
      };
      const id = await createBooking(ctx, payload);
      clearDraft(draftScope);
      toast.success("Request submitted and routed to your Venson team.");
      nav(`/driver/booking/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || "We couldn't submit your request. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell back="/driver" title="Service & repair">
      <Panel className="p-3 mb-4 text-sm text-muted-foreground">
        For <span className="vda-reg text-foreground">{ctx.vehicle?.registration}</span> · {ctx.customer?.name}. Your request routes automatically — you don't need to choose a customer or pod.
      </Panel>
      <form onSubmit={submit} className="space-y-4" data-testid="book-form">
        <Field label="What's needed?">
          <Select data-testid="book-service-type" value={form.service_type} onChange={(e) => set("service_type", e.target.value)}>
            {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        </Field>

        <Field label="Current mileage">
          <Input data-testid="book-mileage" inputMode="numeric" value={form.mileage}
            onChange={(e) => set("mileage", e.target.value.replace(/\D/g, ""))} placeholder="e.g. 42000" />
        </Field>
        {mileageWarning && (
          <Panel className="p-3 -mt-2 text-sm bg-[hsl(var(--vda-amber-bg))] border-[hsl(var(--vda-amber))]/30 flex gap-2" data-testid="mileage-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--vda-amber))] flex-none" />
            <span className="text-foreground/80">{mileageWarning} We'll still submit it — your Venson team can confirm.</span>
          </Panel>
        )}

        <div>
          <span className="mb-1.5 block vda-tick text-muted-foreground">Preferred dates</span>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3].map((n) => (
              <Input key={n} data-testid={`book-date-${n}`} type="date" value={form[`preferred_date_${n}`]}
                onChange={(e) => set(`preferred_date_${n}`, e.target.value)} />
            ))}
          </div>
          <span className="mt-1 block text-xs text-muted-foreground">Give up to three options — we'll try your first choice first.</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="vda-panel p-3 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="book-collection" checked={form.collection_required} onChange={(e) => set("collection_required", e.target.checked)} />
            <span className="text-sm">Collection required</span>
          </label>
          <label className="vda-panel p-3 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="book-courtesy" checked={form.courtesy_vehicle_required} onChange={(e) => set("courtesy_vehicle_required", e.target.checked)} />
            <span className="text-sm">Courtesy vehicle</span>
          </label>
        </div>

        {form.collection_required ? (
          <AddressLookup label="Collection address" value={form.address} onChange={(a) => set("address", a)} testIdPrefix="book-address" />
        ) : (
          <AddressLookup label="Where is the vehicle based?" value={form.address} onChange={(a) => set("address", a)} testIdPrefix="book-address" />
        )}

        <Field label="Additional information">
          <Textarea data-testid="book-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Anything the garage should know (symptoms, warning lights, history)" />
        </Field>
        <Btn type="submit" loading={saving} data-testid="book-submit" className="w-full">Submit request</Btn>
      </form>
    </DriverShell>
  );
}
